import { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../store';
import { convertFromReduxSafeUserResult, convertFromReduxSafeUserState, updateUserFields, ReduxSafeUserDataResult } from './slice';
import { UserGroup } from '../../../interfaces/db';
import { UserDataResult } from '../../../interfaces/users';

export interface UseUserDataReturn {
  // Raw user data 
  userRaw: RootState['user']['value'];
  // Current user data (converted)
  user: ReturnType<typeof convertFromReduxSafeUserState> | null;
  // All users (no filtering - for backward compatibility)
  allUsers: ReturnType<typeof convertFromReduxSafeUserResult>[];
  // All visible users (active dialers only - can_dial && team_is_active)
  allVisibleUsers: ReturnType<typeof convertFromReduxSafeUserResult>[];
  // All active dialer users (can_dial && team_is_active)
  allActiveDialerUsers: ReturnType<typeof convertFromReduxSafeUserResult>[];
  // All active team users (team_is_active only)
  allActiveTeamUsers: ReturnType<typeof convertFromReduxSafeUserResult>[];
  // All parallel enabled users (can_dial && parallel_enabled && team_is_active)
  allParallelEnabledUsers: ReturnType<typeof convertFromReduxSafeUserResult>[];
  // Raw visible accounts (for compatibility)
  visibleAccounts: RootState['visibleAccounts']['value'];
  // User group information
  sortedUserGroupInfo: NonNullable<RootState['userGroupInfo']['sortedGroupInfo']>;
  userGroupInfo: RootState['userGroupInfo'];
  // Team data
  team: RootState['team']['value'];
  // Team permission flags
  canText: boolean;
  canProvision: boolean;
  isAdmin: boolean | null;
  // Custom definitions
  customDefinitions: NonNullable<RootState['customDefinitions']['value']>;
  // Derived data
  allVisibleUserIds: string[];
  allActiveDialerUserIds: string[];
  allActiveTeamUserIds: string[];
  // Specific flags and properties
  userId: string | undefined;
  teamId: string | undefined;
  userLoaded: boolean;
  userGroupsLoaded: boolean;
  // Maps for quick lookups (based on all active team users)
  userIdToUser: Map<string, ReturnType<typeof convertFromReduxSafeUserResult>>;
  userIdToGroups: Map<string, UserGroup[]>;
  // Functions
  updateUser: (userId: string, updates: Partial<UserDataResult>) => void;
}

// Create memoized selectors to prevent unnecessary re-renders
const selectAllUsers = createSelector(
  (state: RootState) => state.visibleAccounts.value?.users,
  (users) => {
    if (!users) return [];
    return users.map(u => convertFromReduxSafeUserResult(u));
  }
);

const selectIsAdmin = createSelector(
  (state: RootState) => state.adminWrite.value,
  (adminWrite) => {
    if (adminWrite === null) return null;
    return adminWrite.team_ids.length > 0;
  }
);

const selectCurrentUser = createSelector(
  (state: RootState) => state.user,
  (userState) => convertFromReduxSafeUserState(userState)
);

const selectSortedUserGroupInfo = createSelector(
  (state: RootState) => state.userGroupInfo.sortedGroupInfo,
  (sortedGroupInfo) => sortedGroupInfo ?? []
);

const selectCustomDefinitions = createSelector(
  (state: RootState) => state.customDefinitions.value,
  (customDefs) => customDefs ?? []
);

/**
 * Centralized hook for accessing user-related data with proper memoization
 * to prevent unnecessary re-renders from Redux selectors
 */
export const useUserData = (): UseUserDataReturn => {
  const dispatch = useDispatch();
  
  // Raw user data
  const userRaw = useSelector((state: RootState) => state.user.value);
  const visibleAccounts = useSelector((state: RootState) => state.visibleAccounts.value);
  const userGroupInfo = useSelector((state: RootState) => state.userGroupInfo);

  // Team permission flags
  const canText = useSelector((state: RootState) => state.team.can_text);
  const canProvision = useSelector((state: RootState) => state.team.can_provision);
  const isAdmin = useSelector(selectIsAdmin);

  // Team data
  const team = useSelector((state: RootState) => state.team.value);

  // Custom definitions
  const customDefinitions = useSelector(selectCustomDefinitions);

  // Get current user (converted)
  const user = useSelector(selectCurrentUser);

  // Get all users (no filtering) - using memoized selector
  const allUsers = useSelector(selectAllUsers);

  // Derive different user filtering patterns with memoization
  const allActiveDialerUsers = useMemo(() => 
    allUsers.filter(u => u.can_dial && u.team_is_active), 
    [allUsers]
  );

  const allActiveTeamUsers = useMemo(() => 
    allUsers.filter(u => u.team_is_active), 
    [allUsers]
  );

  const allParallelEnabledUsers = useMemo(() => 
    allUsers.filter(u => u.can_dial && u.parallel_enabled && u.team_is_active), 
    [allUsers]
  );

  // For backward compatibility, allVisibleUsers is same as allActiveDialerUsers
  const allVisibleUsers = allActiveDialerUsers;

  // Get sorted user group info with fallback
  const sortedUserGroupInfo = useSelector(selectSortedUserGroupInfo);

  // Derived data - user ID arrays
  const allVisibleUserIds = useMemo(() => 
    allVisibleUsers.map(user => user.user_id), 
    [allVisibleUsers]
  );

  const allActiveDialerUserIds = useMemo(() => 
    allActiveDialerUsers.map(user => user.user_id), 
    [allActiveDialerUsers]
  );

  const allActiveTeamUserIds = useMemo(() => 
    allActiveTeamUsers.map(user => user.user_id), 
    [allActiveTeamUsers]
  );

  const userId = userRaw?.user_id;
  const teamId = userRaw?.team_id;
  const userLoaded = userRaw !== null;
  const userGroupsLoaded = userGroupInfo.hasLoaded;

  // Create maps for quick lookups - use allActiveTeamUsers for broader coverage
  const userIdToUser = useMemo(() => 
    new Map(allActiveTeamUsers.map(user => [user.user_id, user])), 
    [allActiveTeamUsers]
  );

  const userIdToGroups = useMemo(() => {
    const memberships = userGroupInfo.memberships.filter(m => !m.member_is_group);
    const result = new Map<string, UserGroup[]>();
    
    for (const membership of memberships) {
      const userId = membership.member_id;
      const groupInfo = sortedUserGroupInfo[userGroupInfo.groupIdToSortedIdx[membership.user_group_id]];
      
      if (!result.has(userId)) {
        result.set(userId, []);
      }
      if (groupInfo) {
        result.get(userId)!.push(groupInfo.group);
      }
    }
    
    return result;
  }, [userGroupInfo.memberships, sortedUserGroupInfo, userGroupInfo.groupIdToSortedIdx]);

  // Function to update user fields
  const updateUser = useMemo(() => (userId: string, updates: Partial<UserDataResult>) => {
    // Convert UserDataResult updates to ReduxSafeUserDataResult format
    const reduxSafeUpdates: Partial<ReduxSafeUserDataResult> = {};
    
    Object.keys(updates).forEach(key => {
      const value = (updates as any)[key];
      if (key === 'created_at' && value instanceof Date) {
        reduxSafeUpdates.created_at = value.valueOf();
      } else {
        (reduxSafeUpdates as any)[key] = value;
      }
    });
    
    dispatch(updateUserFields({ userId, updates: reduxSafeUpdates }));
  }, [dispatch]);

  return {
    userRaw,
    user,
    allUsers,
    allVisibleUsers,
    allActiveDialerUsers,
    allActiveTeamUsers,
    allParallelEnabledUsers,
    visibleAccounts,
    sortedUserGroupInfo,
    userGroupInfo,
    team,
    canText,
    canProvision,
    isAdmin,
    customDefinitions,
    allVisibleUserIds,
    allActiveDialerUserIds,
    allActiveTeamUserIds,
    userId,
    teamId,
    userLoaded,
    userGroupsLoaded,
    userIdToUser,
    userIdToGroups,
    updateUser
  };
};